export type OptionList = 'A' | 'B' | 'C';

export interface VisaStatusSummary {
  code: string;
  label: string;
  guidance: string | null;
}

/** One document choice for a visa status; a combination lists each of its documents. */
export interface DocumentOptionEntry {
  list: OptionList;
  /** The Section 2 document key the choice is saved under. */
  value: string;
  label: string;
  /** The Visa Handling Matrix document, sent back with the save. */
  documentTypeCode: string;
  components: { code: string; label: string }[];
  /**
   * The Section 2 keys that stand for this very document. A key picked from the lists matches the
   * option only through these; a combination saved under another document's key has none.
   */
  ownKeys: string[];
}

export interface DocumentOptions {
  ruleDate: string;
  statuses: VisaStatusSummary[];
  status: (VisaStatusSummary & { additionalInformation: string[] }) | null;
  options: {
    entries: DocumentOptionEntry[];
    anyOfLists: OptionList[];
    supporting: { code: string; label: string }[];
  } | null;
}

export interface ChosenDocuments {
  listA?: string | null;
  listB?: string | null;
  listC?: string | null;
  documentTypeCodes?: Partial<Record<OptionList, string | null>>;
}

/**
 * Whether the documents chosen are one of the status's options, by the same rule the server records
 * (VTK-2037): the same matrix document where one was picked, else a key that stands for the option's
 * own document; any document where the status accepts any of a list. A List B + List C combination is
 * always one: the employee chooses which acceptable documents to present (M-274 4.0). Undecided (null)
 * until a List A, or a List B and a List C, is chosen.
 */
export function isChoiceInMatrix(
  options: Pick<NonNullable<DocumentOptions['options']>, 'entries' | 'anyOfLists'>,
  chosen: ChosenDocuments
): boolean | null {
  const offered = (list: OptionList, key: string) => {
    if (options.anyOfLists.includes(list)) return true;
    const code = chosen.documentTypeCodes?.[list];
    return options.entries.some(
      (e) =>
        e.list === list && (code ? e.documentTypeCode === code : (e.ownKeys ?? []).includes(key))
    );
  };
  if (chosen.listA) return offered('A', chosen.listA);
  if (chosen.listB && chosen.listC) return true;
  return null;
}

/**
 * "Unknown" in the visa status picker (VTK-2136). It is never a status: the picker holds it so the
 * choice shows, and the save sends no status. It cannot pass the server's status code pattern, so a
 * leak is refused rather than stored.
 */
export const UNKNOWN_STATUS = '__unknown__';

/** The status code a save sends: none for Unknown or no choice. */
export function statusToSave(code: string | null | undefined): string {
  return code && code !== UNKNOWN_STATUS ? code : '';
}
